import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';



const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Main Slider Container
const SliderContainer = styled.section`
  height: 100vh;
  position: relative;
  overflow: hidden;
  background: #f8f9fa;
`;

const SliderTrack = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${props => -props.currentSlide * 100}%);
`;

// Individual Slide
const Slide = styled.div`
  min-width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 5%;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => `url(${props.bgImage}) no-repeat center center`};
    background-size: cover;
    opacity: 0.12;
    transition: opacity 0.8s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #6c757d, transparent);
  }
`;

// Slide Content
const SlideContent = styled.div`
  max-width: 900px;
  z-index: 2;
  opacity: ${props => props.isActive ? 1 : 0};
  transform: ${props => props.isActive ? 'translateX(0)' : 'translateX(-50px)'};
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transition-delay: ${props => props.isActive ? '0.3s' : '0s'};
`;

const SlideTitle = styled.h1`
  font-size: 6rem;
  
  line-height: 1.05;
  color: #2c2c2c;
  margin-bottom: 40px;
  letter-spacing: -3px;
  
  strong {
    font-weight: 700;
    color: #2B4B8C;
    background: linear-gradient(135deg, #2B4B8C, #3B82C4);
    background-size: 200% 200%;
    animation: ${gradientShift} 3s ease infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    font-size: 3.5rem;
    letter-spacing: -2px;
  }
`;

const SlideSubtitle = styled.p`
  font-size: 1.4rem;
  color: #6c757d;
  margin-bottom: 60px;
  max-width: 600px;
  line-height: 1.7;
  
`;


const SliderControls = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 20px;
  z-index: 10;
  
  @media (max-width: 768px) {
    bottom: 20px;
  }
`;

const PlayPauseButton = styled.button`
  width: 50px;
  height: 50px;
  background: rgba(43, 75, 140, 0.9);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(43, 75, 140, 1);
    transform: scale(1.1);
  }
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    width: 150px;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #2B4B8C, #3B82C4);
  border-radius: 2px;
  transition: width 0.1s linear;
  width: ${props => (props.progress * 100)}%;
`;

const SlideIndicators = styled.div`
  display: flex;
  gap: 8px;
`;

const SlideIndicator = styled.button`
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: ${props => props.isActive ? 'rgba(43, 75, 140, 1)' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.isActive ? 'rgba(43, 75, 140, 1)' : 'rgba(255, 255, 255, 0.8)'};
    transform: scale(1.2);
  }
`;

// Navigation Arrows
const NavArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  font-size: 1.5rem;
  color: #2B4B8C;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  ${props => props.direction === 'prev' ? 'left: 30px;' : 'right: 30px;'}
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
    ${props => props.direction === 'prev' ? 'left: 15px;' : 'right: 15px;'}
  }
`;

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const { t } = useLanguage();

  const slides = [
    {
      id: 1,
      title: <>{t('hero.slide1Title')} <strong>{t('hero.slide1TitleBold')}</strong> {t('hero.slide1TitleEnd')}</>,
      subtitle: t('hero.slide1Subtitle'),
      buttonText: t('hero.slide1Button'),
      buttonLink: "/urunler",
      bgImage: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 2,
      title: <>{t('hero.slide2Title')} <strong>{t('hero.slide2TitleBold')}</strong> {t('hero.slide2TitleEnd')}</>,
      subtitle: t('hero.slide2Subtitle'),
      buttonText: t('hero.slide2Button'),
      buttonLink: "/fabrikalar",
      bgImage: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    },
    {
      id: 3,
      title: <>{t('hero.slide3Title')} <strong>{t('hero.slide3TitleBold')}</strong> {t('hero.slide3TitleEnd')}</>,
      subtitle: t('hero.slide3Subtitle'),
      buttonText: t('hero.slide3Button'),
      buttonLink: "/iletisim",
      bgImage: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80"
    }
  ];

  const SLIDE_DURATION = 6000; // 6 seconds

  useEffect(() => {
    let interval;
    let progressInterval;

    if (isPlaying) {
      // Progress bar animation
      progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 1) {
            return 0;
          }
          return prev + (100 / SLIDE_DURATION);
        });
      }, 100);

      // Slide change
      interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slides.length);
        setProgress(0);
      }, SLIDE_DURATION);
    }

    return () => {
      clearInterval(interval);
      clearInterval(progressInterval);
    };
  }, [isPlaying, slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
    setProgress(0);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  return (
    <SliderContainer>
      <SliderTrack currentSlide={currentSlide}>
        {slides.map((slide, index) => (
          <Slide key={slide.id} bgImage={slide.bgImage}>
            <SlideContent isActive={index === currentSlide}>
              <SlideTitle>{slide.title}</SlideTitle>
              <SlideSubtitle>{slide.subtitle}</SlideSubtitle>
            </SlideContent>
          </Slide>
        ))}
      </SliderTrack>

      <NavArrow direction="prev" onClick={prevSlide}>
        ‹
      </NavArrow>
      <NavArrow direction="next" onClick={nextSlide}>
        ›
      </NavArrow>

      <SliderControls>
        <PlayPauseButton onClick={togglePlayPause}>
          {isPlaying ? '⏸️' : '▶️'}
        </PlayPauseButton>
        
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>
        
        <SlideIndicators>
          {slides.map((_, index) => (
            <SlideIndicator
              key={index}
              isActive={index === currentSlide}
              onClick={() => goToSlide(index)}
            />
          ))}
        </SlideIndicators>
      </SliderControls>
    </SliderContainer>
  );
};

export default HeroSlider;
